import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/pages/home/components/ArticleCard";
import { articles, categories } from "@/mocks/mangaData";

export default function CategoryPage() {
  const { id } = useParams<{ id: string }>();
  const category = categories.find((c) => c.id === id);

  const list = useMemo(() => {
    if (!category) return [];
    return articles.filter((a) => a.categoryId === category.id);
  }, [category]);

  const hotArticles = useMemo(
    () => [...articles].sort((a, b) => b.views - a.views).slice(0, 5),
    [],
  );

  const jsonLd = useMemo(() => {
    if (!category) return [];
    return [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: `${category.name} - 日漫情报站`,
        url: `https://animesync.github.io/category/${category.id}`,
        inLanguage: "zh-CN",
        description: category.description,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://animesync.github.io/" },
          { "@type": "ListItem", position: 2, name: category.name, item: `https://animesync.github.io/category/${category.id}` },
        ],
      },
    ];
  }, [category]);

  return (
    <>
      {category ? (
        <Seo
          title={`${category.name} - 日漫最新话·日漫连载更新情报 | 日漫情报站`}
          description={category.description}
          keywords={`${category.name},日漫,日漫最新话,日漫连载,日漫更新,日漫情报`}
          path={`/category/${category.id}`}
          type="article"
          jsonLd={jsonLd}
        />
      ) : (
        <Seo
          title="日漫分类 - 日漫情报站"
          description="浏览日漫情报站的全部分类，包括最新话、连载中、已完结、动画化、情报剧透与休刊公告。"
          keywords="日漫,日漫分类,日漫连载,日漫更新,日漫情报"
          path="/category"
        />
      )}

      {!category ? (
        <section className="mx-auto max-w-7xl px-4 py-20 text-center md:px-6">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-compass-3-line text-3xl" />
          </span>
          <h1 className="mt-6 font-heading text-3xl text-foreground-950">未找到该分类</h1>
          <p className="mt-3 text-sm text-foreground-600">该分类可能已调整，你可以返回首页或浏览其他分类。</p>
          <Link
            to="/"
            className="mt-6 inline-flex whitespace-nowrap items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-600"
          >
            <i className="ri-home-5-line" /> 返回首页
          </Link>
        </section>
      ) : (
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-6">
          <Breadcrumb
            items={[
              { label: "首页", path: "/" },
              { label: category.name },
            ]}
          />

          <header className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="flex items-center gap-3 font-heading text-3xl text-foreground-950 md:text-4xl">
                <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-500 text-white">
                  <i className={`${category.icon} text-2xl`} />
                </span>
                {category.name}
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">{category.description}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-2 rounded-full bg-background-100 px-4 py-2 text-sm text-foreground-700">
                <i className="ri-file-list-3-line" />
                <strong className="text-foreground-950">{list.length}</strong> 篇内容
              </span>
            </div>
          </header>

          {/* 分类切换 */}
          <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="切换分类">
            {categories.map((cat) => {
              const active = cat.id === category.id;
              return (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  role="tab"
                  aria-selected={active}
                  className={`inline-flex whitespace-nowrap items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${
                    active
                      ? "bg-primary-500 text-white"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  <i className={cat.icon} />
                  {cat.name}
                </Link>
              );
            })}
          </div>

          {/* 内容区 */}
          <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2">
              {list.length > 0 ? (
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  {list.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              ) : (
                <div className="rounded-lg border border-background-200 bg-background-50 p-10 text-center">
                  <p className="text-foreground-600">该分类暂时还没有内容，敬请期待。</p>
                </div>
              )}
            </div>

            <aside className="space-y-6">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-lg text-foreground-950">热门文章</h2>
                <div className="mt-4 space-y-3">
                  {hotArticles.map((article, index) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="group flex items-start gap-3"
                    >
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-secondary-100 font-heading text-sm text-secondary-900">
                        {index + 1}
                      </span>
                      <span className="line-clamp-2 text-sm leading-snug text-foreground-700 transition-colors group-hover:text-primary-700">
                        {article.title}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="font-heading text-lg text-foreground-950">追更小贴士</h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  关注本站各分类页面，第一时间掌握日漫最新话、连载更新、情报剧透与休刊提醒，追更不再错过任何一话。
                </p>
                <Link
                  to="/about#contact"
                  className="mt-4 inline-flex whitespace-nowrap items-center gap-2 rounded-full bg-secondary-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-secondary-600"
                >
                  <i className="ri-mail-send-line" /> 订阅资讯
                </Link>
              </div>
            </aside>
          </div>
        </div>
      )}
    </>
  );
}